
import { useEffect, useMemo, useRef, useState } from 'react';
import { cacheGet } from '../utils/cache';

// Normalisation : string → mapping { value: path }
function normalizePaths(arg) {
  if (typeof arg === 'string') {
    return { value: arg, __single__: true };
  }
  // On suppose que arg est un objet { alias: path, ... }
  return { ...arg, __single__: false };
}

export function useCache(arg) {
  const pathsMap = useMemo(() => normalizePaths(arg), [arg]);
  const isSingle = pathsMap.__single__ === true;

  const aliases = useMemo(
    () => Object.keys(pathsMap).filter(k => k !== '__single__'),
    [pathsMap]
  );

  const pathsByAlias = useMemo(() => {
    const m = new Map();
    for (const alias of aliases) m.set(alias, pathsMap[alias]);
    return m;
  }, [aliases, pathsMap]);

  const topicsWeCare = useMemo(() => {
    // Nous écoutons les topics exacts des paths + 'bootstrap' (update global)
    const s = new Set(['bootstrap']);
    for (const p of pathsByAlias.values()) s.add(p);
    return s;
  }, [pathsByAlias]);

  const read = () => {
    if (isSingle) {
      const p = pathsByAlias.get('value');
      return cacheGet(p);
    }
    const result = {};
    for (const [alias, p] of pathsByAlias.entries()) {
      result[alias] = cacheGet(p);
    }
    return result;
  };

  const [state, setState] = useState(read);

  // Coalescer les updates dans un même frame pour éviter le jitter
  const rafIdRef = useRef(0);
  const pendingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    function scheduleRead() {
      console.log('📚 [useCache] scheduleRead appelé, pending:', pendingRef.current);
      if (pendingRef.current) {
        console.log('📚 [useCache] scheduleRead ignoré (déjà en cours)');
        return;
      }
      pendingRef.current = true;
      console.log('📚 [useCache] Programmation requestAnimationFrame...');
      rafIdRef.current = window.requestAnimationFrame(() => {
        // 🔥 FIX: Vérifier que le composant est encore monté avant setState
        if (!isMountedRef.current) {
          console.log('⚠️ [useCache] Composant démonté, ignore setState');
          pendingRef.current = false;
          return;
        }
        
        console.log('📚 [useCache] ✅ EXÉCUTION read() et setState...');
        pendingRef.current = false;
        const newData = read();
        console.log('📚 [useCache] Nouvelles données lues:', newData);
        
        // Double vérification avant setState
        if (isMountedRef.current) {
          setState(newData);
          console.log('📚 [useCache] ✅ setState terminé - composant devrait se re-render');
        }
      });
    }

    // Helper pour vérifier si un topic matche nos besoins
    function topicMatchesRequested(topic) {
      // Bootstrap met à jour tout
      if (topic === 'bootstrap') return true;
      // Match exact
      if (topicsWeCare.has(topic)) return true;

      // Robustesse: si on reçoit un topic "prefix" (ex: 'activity'), on rafraîchit
      // toutes les clés qui commencent par ce préfixe.
      const prefixes = Array.from(topicsWeCare).map(k => k.split('/')[0]);
      return prefixes.some(p => topic === p);
    }

    const onUpd = (e) => {
      const topic = e?.detail?.topic;
      
      console.log('🔔 [useCache] Événement reçu:', { 
        topic, 
        topicsWeCare: Array.from(topicsWeCare),
        detail: e.detail 
      });
      
      if (!topic) return;
      
      const matches = topicMatchesRequested(topic);
      
      console.log('🔔 [useCache] Topic matche?', { topic, matches });
      
      if (matches) {
        console.log('🔔 [useCache] ✅ DÉCLENCHEMENT scheduleRead pour topic:', topic);
        scheduleRead();
      } else {
        console.log('🔔 [useCache] ❌ PAS DE MATCH pour topic:', topic);
      }
      // Option: si on a des events "batch" plus tard, on pourra regarder e.detail.topics
    };

    window.addEventListener('cc:cache-updated', onUpd);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('cc:cache-updated', onUpd);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
      pendingRef.current = false;
    };
    // NB: pas besoin de dépendre de selectedId etc. ici
  }, [topicsWeCare]); // stable: mis à jour seulement si les paths changent

  return state;
}